import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { usersAPI, companiesAPI, keyResultsAPI, objectivesAPI, authAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Settings, 
  Users, 
  Building2,
  Target,
  RefreshCw,
  Shield,
  Edit3,
  Plus,
  UserPlus,
  Trash2,
  MoreHorizontal
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const AdminPage = () => {
  const { isOwner } = useAuth();
  const [activeTab, setActiveTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [objectives, setObjectives] = useState([]);
  const [keyResults, setKeyResults] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Dialogs
  const [showKRDialog, setShowKRDialog] = useState(false);
  const [editingKR, setEditingKR] = useState(null);
  
  const [showUserDialog, setShowUserDialog] = useState(false);
  const [newUser, setNewUser] = useState({ name: '', email: '', password: '', role: 'contributor' });
  
  const [showEditUserDialog, setShowEditUserDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  
  const [showDeleteUserDialog, setShowDeleteUserDialog] = useState(false);
  const [deletingUser, setDeletingUser] = useState(null);
  
  const [showCompanyDialog, setShowCompanyDialog] = useState(false);
  const [newCompany, setNewCompany] = useState({ name: '', code: '', description: '', visibility: 'public' });
  
  const [showCompanyEditDialog, setShowCompanyEditDialog] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  
  const [showKRCreateDialog, setShowKRCreateDialog] = useState(false);
  const [newKR, setNewKR] = useState({ 
    objective_id: '', 
    company_id: '', 
    title: '', 
    target_value: 100, 
    unit: '%',
    visibility: 'public'
  });

  const fetchData = async () => {
    try {
      setLoading(true);
      const [usersRes, companiesRes, krsRes, objsRes] = await Promise.all([
        usersAPI.getAll().catch(() => ({ data: [] })),
        companiesAPI.getAll(),
        keyResultsAPI.getAll({}),
        objectivesAPI.getAll()
      ]);
      
      setUsers(usersRes.data);
      setCompanies(companiesRes.data);
      setKeyResults(krsRes.data);
      setObjectives(objsRes.data);
    } catch (error) {
      console.error('Error fetching admin data:', error);
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOwner) {
      fetchData();
    }
  }, [isOwner]);

  const handleRoleChange = async (userId, newRole) => {
    try {
      await usersAPI.updateRole(userId, newRole);
      toast.success('Rol actualizado');
      fetchData();
    } catch (error) {
      toast.error('Error al actualizar rol');
    }
  };

  const handleCreateUser = async () => {
    if (!newUser.name || !newUser.email || !newUser.password) {
      toast.error('Completa todos los campos');
      return;
    }
    try {
      await authAPI.register(newUser);
      toast.success('Usuario creado');
      setShowUserDialog(false);
      setNewUser({ name: '', email: '', password: '', role: 'contributor' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear usuario');
    }
  };

  const handleUpdateUser = async () => {
    if (!editingUser) return;
    try {
      await usersAPI.update(editingUser.user_id, { name: editingUser.name });
      toast.success('Usuario actualizado');
      setShowEditUserDialog(false);
      setEditingUser(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar usuario');
    }
  };

  const handleDeleteUser = async () => {
    if (!deletingUser) return;
    try {
      await usersAPI.delete(deletingUser.user_id);
      toast.success('Usuario eliminado');
      setShowDeleteUserDialog(false);
      setDeletingUser(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar usuario');
    }
  };

  const handleCreateCompany = async () => {
    if (!newCompany.name || !newCompany.code) {
      toast.error('Nombre y código son requeridos');
      return;
    }
    try {
      await companiesAPI.create(newCompany);
      toast.success('Empresa creada');
      setShowCompanyDialog(false);
      setNewCompany({ name: '', code: '', description: '', visibility: 'public' });
      fetchData();
    } catch (error) {
      toast.error('Error al crear empresa');
    }
  };

  const handleUpdateCompany = async () => {
    if (!editingCompany) return;
    try {
      await companiesAPI.update(editingCompany.company_id, {
        name: editingCompany.name,
        code: editingCompany.code,
        description: editingCompany.description,
        visibility: editingCompany.visibility
      });
      toast.success('Empresa actualizada');
      setShowCompanyEditDialog(false);
      setEditingCompany(null);
      fetchData();
    } catch (error) {
      toast.error('Error al actualizar empresa');
    }
  };

  const handleCreateKR = async () => {
    if (!newKR.title || !newKR.objective_id || !newKR.company_id) {
      toast.error('Completa todos los campos requeridos');
      return;
    }
    try {
      await keyResultsAPI.create(newKR);
      toast.success('Key Result creado');
      setShowKRCreateDialog(false);
      setNewKR({ objective_id: '', company_id: '', title: '', target_value: 100, unit: '%', visibility: 'public' });
      fetchData();
    } catch (error) {
      toast.error('Error al crear KR');
    }
  };

  const handleUpdateKR = async () => {
    if (!editingKR) return;
    
    try {
      await keyResultsAPI.update(editingKR.kr_id, {
        current_value: editingKR.current_value,
        status: editingKR.status,
        owner_id: editingKR.owner_id
      });
      toast.success('KR actualizado');
      setShowKRDialog(false);
      fetchData();
    } catch (error) {
      toast.error('Error al actualizar KR');
    }
  };

  const getCompanyName = (companyId) => {
    const company = companies.find(c => c.company_id === companyId);
    return company?.code || 'N/A';
  };

  if (!isOwner) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
        <Shield className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <h2 className="text-lg font-medium text-gray-900 mb-2">Acceso Restringido</h2>
        <p className="text-gray-500">Solo el Owner puede acceder al panel de administración.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-blue-500/30 border-t-blue-500 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Settings className="w-6 h-6 text-gray-700" />
            Admin Panel
          </h1>
          <p className="text-gray-500 mt-1">
            Gestión de usuarios, empresas y OKRs
          </p>
        </div>
        <Button 
          variant="outline" 
          size="icon"
          className="border-gray-200 hover:bg-gray-100"
          onClick={fetchData}
        >
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-gray-200 pb-4">
        <Button
          variant={activeTab === 'users' ? 'default' : 'ghost'}
          onClick={() => setActiveTab('users')}
          className={activeTab === 'users' ? 'bg-gray-900 text-white' : 'text-gray-600 hover:text-gray-900'}
          data-testid="admin-users-tab"
        >
          <Users className="w-4 h-4 mr-2" />
          Usuarios ({users.length})
        </Button>
        <Button
          variant={activeTab === 'companies' ? 'default' : 'ghost'}
          onClick={() => setActiveTab('companies')}
          className={activeTab === 'companies' ? 'bg-gray-900 text-white' : 'text-gray-600 hover:text-gray-900'}
          data-testid="admin-companies-tab"
        >
          <Building2 className="w-4 h-4 mr-2" />
          Empresas ({companies.length})
        </Button>
        <Button
          variant={activeTab === 'krs' ? 'default' : 'ghost'}
          onClick={() => setActiveTab('krs')}
          className={activeTab === 'krs' ? 'bg-gray-900 text-white' : 'text-gray-600 hover:text-gray-900'}
          data-testid="admin-krs-tab"
        >
          <Target className="w-4 h-4 mr-2" />
          Key Results ({keyResults.length})
        </Button>
      </div>

      {/* Users Tab */}
      {activeTab === 'users' && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-medium text-gray-900">Gestión de Usuarios</h2>
            <Button onClick={() => setShowUserDialog(true)} className="bg-gray-900 hover:bg-gray-800 text-white" data-testid="add-user-btn">
              <UserPlus className="w-4 h-4 mr-2" />
              Agregar Usuario
            </Button>
          </div>
          
          {users.length > 0 ? (
            <div className="space-y-3">
              {users.map((user) => (
                <div 
                  key={user.user_id}
                  className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-100"
                >
                  <div className="flex items-center gap-3">
                    {user.picture ? (
                      <img 
                        src={user.picture} 
                        alt={user.name}
                        className="w-10 h-10 rounded-full"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-bold">
                        {user.name?.charAt(0)?.toUpperCase()}
                      </div>
                    )}
                    <div>
                      <p className="text-gray-900 font-medium">{user.name}</p>
                      <p className="text-sm text-gray-500">{user.email}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <Select 
                      value={user.role} 
                      onValueChange={(v) => handleRoleChange(user.user_id, v)}
                    >
                      <SelectTrigger className="w-40 bg-white border-gray-200">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="owner">Owner</SelectItem>
                        <SelectItem value="executive">Executive</SelectItem>
                        <SelectItem value="contributor">Contributor</SelectItem>
                      </SelectContent>
                    </Select>
                    
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <MoreHorizontal className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="bg-white border-gray-200">
                        <DropdownMenuItem
                          onClick={() => {
                            setEditingUser({ ...user });
                            setShowEditUserDialog(true);
                          }}
                          className="cursor-pointer"
                        >
                          <Edit3 className="w-4 h-4 mr-2" />
                          Editar nombre
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => {
                            setDeletingUser(user);
                            setShowDeleteUserDialog(true);
                          }}
                          className="cursor-pointer text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Eliminar usuario
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-4">No hay usuarios registrados</p>
          )}
        </div>
      )}

      {/* Companies Tab */}
      {activeTab === 'companies' && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-medium text-gray-900">Empresas</h2>
            <Button onClick={() => setShowCompanyDialog(true)} className="bg-gray-900 hover:bg-gray-800 text-white" data-testid="add-company-btn">
              <Plus className="w-4 h-4 mr-2" />
              Agregar Empresa
            </Button>
          </div>
          
          <div className="space-y-3">
            {companies.map((company) => (
              <div 
                key={company.company_id}
                className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-100"
              >
                <div className="flex items-center gap-3">
                  <div 
                    className="w-12 h-12 rounded-lg bg-cover bg-center bg-gray-200"
                    style={{ backgroundImage: company.image_url ? `url(${company.image_url})` : 'none' }}
                  />
                  <div>
                    <p className="text-gray-900 font-medium">{company.name}</p>
                    <p className="text-sm text-gray-500">{company.code}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-xs px-2 py-1 rounded ${
                    company.visibility === 'private' 
                      ? 'bg-red-100 text-red-700' 
                      : 'bg-emerald-100 text-emerald-700'
                  }`}>
                    {company.visibility}
                  </span>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setEditingCompany({ ...company });
                      setShowCompanyEditDialog(true);
                    }}
                    data-testid={`edit-company-${company.company_id}`}
                  >
                    <Edit3 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Key Results Tab */}
      {activeTab === 'krs' && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-medium text-gray-900">Key Results</h2>
            <Button onClick={() => setShowKRCreateDialog(true)} className="bg-gray-900 hover:bg-gray-800 text-white" data-testid="add-kr-btn">
              <Plus className="w-4 h-4 mr-2" />
              Agregar KR
            </Button>
          </div>
          
          <div className="space-y-3 max-h-[600px] overflow-y-auto">
            {keyResults.slice(0, 50).map((kr) => (
              <div 
                key={kr.kr_id}
                className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-100"
              >
                <div className="flex-1 min-w-0 mr-4">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs px-2 py-0.5 bg-gray-200 rounded text-gray-700">
                      {getCompanyName(kr.company_id)}
                    </span>
                    <span className={`text-xs px-2 py-0.5 rounded ${
                      kr.status === 'on_track' ? 'bg-emerald-100 text-emerald-700' :
                      kr.status === 'at_risk' ? 'bg-amber-100 text-amber-700' :
                      kr.status === 'blocked' ? 'bg-red-100 text-red-700' :
                      'bg-gray-100 text-gray-600'
                    }`}>
                      {kr.status}
                    </span>
                  </div>
                  <p className="text-sm text-gray-900 truncate">{kr.title}</p>
                  <p className="text-xs text-gray-500 font-mono mt-1">
                    {kr.current_value}/{kr.target_value} {kr.unit}
                  </p>
                </div>
                
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setEditingKR(kr);
                    setShowKRDialog(true);
                  }}
                  data-testid={`edit-kr-${kr.kr_id}`}
                >
                  <Edit3 className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Add User Dialog */}
      <Dialog open={showUserDialog} onOpenChange={setShowUserDialog}>
        <DialogContent className="bg-white border-gray-200">
          <DialogHeader>
            <DialogTitle className="text-gray-900">Agregar Usuario</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Input
              placeholder="Nombre"
              value={newUser.name}
              onChange={(e) => setNewUser(prev => ({ ...prev, name: e.target.value }))}
              className="bg-gray-50 border-gray-200 focus:bg-white"
              data-testid="new-user-name"
            />
            <Input
              type="email"
              placeholder="Email"
              value={newUser.email}
              onChange={(e) => setNewUser(prev => ({ ...prev, email: e.target.value }))}
              className="bg-gray-50 border-gray-200 focus:bg-white"
              data-testid="new-user-email"
            />
            <Input
              type="password"
              placeholder="Contraseña"
              value={newUser.password}
              onChange={(e) => setNewUser(prev => ({ ...prev, password: e.target.value }))}
              className="bg-gray-50 border-gray-200 focus:bg-white"
              data-testid="new-user-password"
            />
            <Select 
              value={newUser.role} 
              onValueChange={(v) => setNewUser(prev => ({ ...prev, role: v }))}
            >
              <SelectTrigger className="bg-gray-50 border-gray-200">
                <SelectValue placeholder="Rol" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="owner">Owner</SelectItem>
                <SelectItem value="executive">Executive</SelectItem>
                <SelectItem value="contributor">Contributor</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={handleCreateUser} className="w-full bg-gray-900 hover:bg-gray-800 text-white" data-testid="save-user-btn">
              Crear Usuario
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={showEditUserDialog} onOpenChange={setShowEditUserDialog}>
        <DialogContent className="bg-white border-gray-200">
          <DialogHeader>
            <DialogTitle className="text-gray-900">Editar Usuario</DialogTitle>
          </DialogHeader>
          {editingUser && (
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1.5 block">Nombre</label>
                <Input
                  value={editingUser.name}
                  onChange={(e) => setEditingUser(prev => ({ ...prev, name: e.target.value }))}
                  className="bg-gray-50 border-gray-200 focus:bg-white"
                />
              </div>
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-500">{editingUser.email}</p>
              </div>
              <Button onClick={handleUpdateUser} className="w-full bg-gray-900 hover:bg-gray-800 text-white">
                Guardar Cambios
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete User Dialog */}
      <Dialog open={showDeleteUserDialog} onOpenChange={setShowDeleteUserDialog}>
        <DialogContent className="bg-white border-gray-200">
          <DialogHeader>
            <DialogTitle className="text-red-600">Eliminar Usuario</DialogTitle>
            <DialogDescription className="text-gray-500">
              Esta acción es irreversible.
            </DialogDescription>
          </DialogHeader>
          {deletingUser && (
            <div className="space-y-4">
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-700">
                  ¿Estás seguro de eliminar a <strong>{deletingUser.name}</strong> ({deletingUser.email})?
                </p>
              </div>
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  onClick={() => setShowDeleteUserDialog(false)} 
                  className="flex-1 border-gray-200"
                >
                  Cancelar
                </Button>
                <Button 
                  onClick={handleDeleteUser} 
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                >
                  Eliminar
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Company Dialog */}
      <Dialog open={showCompanyDialog} onOpenChange={setShowCompanyDialog}>
        <DialogContent className="bg-white border-gray-200">
          <DialogHeader>
            <DialogTitle className="text-gray-900">Agregar Empresa</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Input
              placeholder="Nombre de la empresa"
              value={newCompany.name}
              onChange={(e) => setNewCompany(prev => ({ ...prev, name: e.target.value }))}
              className="bg-gray-50 border-gray-200 focus:bg-white"
              data-testid="new-company-name"
            />
            <Input
              placeholder="Código (ej: UIS, UAL)"
              value={newCompany.code}
              onChange={(e) => setNewCompany(prev => ({ ...prev, code: e.target.value.toUpperCase() }))}
              className="bg-gray-50 border-gray-200 focus:bg-white"
              data-testid="new-company-code"
            />
            <Textarea
              placeholder="Descripción"
              value={newCompany.description}
              onChange={(e) => setNewCompany(prev => ({ ...prev, description: e.target.value }))}
              className="bg-gray-50 border-gray-200 focus:bg-white"
              data-testid="new-company-description"
            />
            <Select 
              value={newCompany.visibility} 
              onValueChange={(v) => setNewCompany(prev => ({ ...prev, visibility: v }))}
            >
              <SelectTrigger className="bg-gray-50 border-gray-200">
                <SelectValue placeholder="Visibilidad" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="public">Pública</SelectItem>
                <SelectItem value="restricted">Restringida</SelectItem>
                <SelectItem value="private">Privada</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={handleCreateCompany} className="w-full bg-gray-900 hover:bg-gray-800 text-white" data-testid="save-company-btn">
              Crear Empresa
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add KR Dialog */}
      <Dialog open={showKRCreateDialog} onOpenChange={setShowKRCreateDialog}>
        <DialogContent className="bg-white border-gray-200">
          <DialogHeader>
            <DialogTitle className="text-gray-900">Agregar Key Result</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Select 
              value={newKR.company_id} 
              onValueChange={(v) => setNewKR(prev => ({ ...prev, company_id: v }))}
            >
              <SelectTrigger className="bg-gray-50 border-gray-200">
                <SelectValue placeholder="Seleccionar empresa" />
              </SelectTrigger>
              <SelectContent>
                {companies.map((company) => (
                  <SelectItem key={company.company_id} value={company.company_id}>
                    {company.code} - {company.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select 
              value={newKR.objective_id} 
              onValueChange={(v) => setNewKR(prev => ({ ...prev, objective_id: v }))}
            >
              <SelectTrigger className="bg-gray-50 border-gray-200">
                <SelectValue placeholder="Seleccionar objetivo" />
              </SelectTrigger>
              <SelectContent>
                {objectives
                  .filter(o => !newKR.company_id || o.company_id === newKR.company_id)
                  .map((obj) => (
                    <SelectItem key={obj.objective_id} value={obj.objective_id}>
                      {obj.title.substring(0, 50)}...
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
            
            <Textarea
              placeholder="Título del Key Result"
              value={newKR.title}
              onChange={(e) => setNewKR(prev => ({ ...prev, title: e.target.value }))}
              className="bg-gray-50 border-gray-200 focus:bg-white"
              data-testid="new-kr-title"
            />
            
            <div className="grid grid-cols-2 gap-4">
              <Input
                type="number"
                placeholder="Target"
                value={newKR.target_value}
                onChange={(e) => setNewKR(prev => ({ ...prev, target_value: parseFloat(e.target.value) }))}
                className="bg-gray-50 border-gray-200 focus:bg-white"
                data-testid="new-kr-target"
              />
              <Input
                placeholder="Unidad (%, días, etc)"
                value={newKR.unit}
                onChange={(e) => setNewKR(prev => ({ ...prev, unit: e.target.value }))}
                className="bg-gray-50 border-gray-200 focus:bg-white"
                data-testid="new-kr-unit"
              />
            </div>
            
            <Button onClick={handleCreateKR} className="w-full bg-gray-900 hover:bg-gray-800 text-white" data-testid="save-kr-btn">
              Crear Key Result
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit KR Dialog */}
      <Dialog open={showKRDialog} onOpenChange={setShowKRDialog}>
        <DialogContent className="bg-white border-gray-200">
          <DialogHeader>
            <DialogTitle className="text-gray-900">Editar Key Result</DialogTitle>
          </DialogHeader>
          {editingKR && (
            <div className="space-y-4">
              <div>
                <span className="text-sm text-gray-500 block mb-2">{editingKR.title}</span>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Valor Actual</label>
                  <Input
                    type="number"
                    value={editingKR.current_value}
                    onChange={(e) => setEditingKR(prev => ({ 
                      ...prev, 
                      current_value: parseFloat(e.target.value) 
                    }))}
                    className="bg-gray-50 border-gray-200 focus:bg-white"
                    data-testid="kr-current-value-input"
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Target</label>
                  <Input
                    type="number"
                    value={editingKR.target_value}
                    disabled
                    className="bg-gray-100 border-gray-200"
                  />
                </div>
              </div>
              
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Status</label>
                <Select 
                  value={editingKR.status} 
                  onValueChange={(v) => setEditingKR(prev => ({ ...prev, status: v }))}
                >
                  <SelectTrigger className="bg-gray-50 border-gray-200">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="on_track">On Track</SelectItem>
                    <SelectItem value="at_risk">At Risk</SelectItem>
                    <SelectItem value="blocked">Blocked</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Owner</label>
                <Select 
                  value={editingKR.owner_id || ''} 
                  onValueChange={(v) => setEditingKR(prev => ({ ...prev, owner_id: v }))}
                >
                  <SelectTrigger className="bg-gray-50 border-gray-200">
                    <SelectValue placeholder="Seleccionar owner" />
                  </SelectTrigger>
                  <SelectContent>
                    {users.map((user) => (
                      <SelectItem key={user.user_id} value={user.user_id}>
                        {user.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <Button onClick={handleUpdateKR} className="w-full bg-gray-900 hover:bg-gray-800 text-white" data-testid="save-kr-btn">
                Guardar Cambios
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Company Dialog */}
      <Dialog open={showCompanyEditDialog} onOpenChange={setShowCompanyEditDialog}>
        <DialogContent className="bg-white border-gray-200">
          <DialogHeader>
            <DialogTitle className="text-gray-900">Editar Empresa</DialogTitle>
          </DialogHeader>
          {editingCompany && (
            <div className="space-y-4">
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Nombre</label>
                <Input
                  placeholder="Nombre de la empresa"
                  value={editingCompany.name}
                  onChange={(e) => setEditingCompany(prev => ({ ...prev, name: e.target.value }))}
                  className="bg-gray-50 border-gray-200 focus:bg-white"
                  data-testid="edit-company-name"
                />
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Código</label>
                <Input
                  placeholder="Código (ej: UIS, UAL)"
                  value={editingCompany.code}
                  onChange={(e) => setEditingCompany(prev => ({ ...prev, code: e.target.value.toUpperCase() }))}
                  className="bg-gray-50 border-gray-200 focus:bg-white"
                  data-testid="edit-company-code"
                />
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Descripción</label>
                <Textarea
                  placeholder="Descripción"
                  value={editingCompany.description || ''}
                  onChange={(e) => setEditingCompany(prev => ({ ...prev, description: e.target.value }))}
                  className="bg-gray-50 border-gray-200 focus:bg-white"
                  data-testid="edit-company-description"
                />
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Visibilidad</label>
                <Select 
                  value={editingCompany.visibility} 
                  onValueChange={(v) => setEditingCompany(prev => ({ ...prev, visibility: v }))}
                >
                  <SelectTrigger className="bg-gray-50 border-gray-200">
                    <SelectValue placeholder="Visibilidad" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="public">Pública</SelectItem>
                    <SelectItem value="restricted">Restringida</SelectItem>
                    <SelectItem value="private">Privada</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={handleUpdateCompany} className="w-full bg-gray-900 hover:bg-gray-800 text-white" data-testid="update-company-btn">
                Guardar Cambios
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPage;
